import { useEffect, useState } from "react";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import TeamSheet from "./TeamSheet";
import { Pill } from "./shared";

export default function TeamsTab({ data, division, isDirector, onChanged }) {
  const t = data.tournament;
  const hub = t.tier === "hub";
  const teams = data.teams.filter((x) => x.division_id === division.id);
  const [names, setNames] = useState("");
  const [busy, setBusy] = useState(false);
  const [openTeam, setOpenTeam] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const players = data.players || [];

  useEffect(() => {
    if (!hub) return;
    tournaments.rosterConflicts(t.id).then((d) => setConflicts(d.conflicts)).catch(() => {});
  }, [hub, t.id, players.length]);

  async function paste() {
    const list = names.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      const d = await tournaments.bulkAddTeams(t.id, list, division.id);
      setNames("");
      await onChanged();
      toast.success(`Added ${d.added.length} team(s).`);
    } catch (e) { toast.error(e.message || "Couldn't add."); }
    finally { setBusy(false); }
  }

  async function build(mode) {
    if (conflicts.length && !window.confirm(
      `${conflicts.length} possible roster conflict(s) found. This is only a warning — build anyway?`)) return;
    try {
      await tournaments.buildDivision(t.id, division.id,
        { mode, seed_order: mode === "manual" ? teams.map((x) => x.id) : undefined });
      await onChanged();
      toast.success("Bracket built.");
    } catch (e) { toast.error(e.message || "Couldn't build."); }
  }

  const team = openTeam && data.teams.find((x) => x.id === openTeam);
  return (
    <div data-testid="teams-tab">
      {hub && <RosterConflicts conflicts={conflicts} />}
      <SectionTitle label={`Teams (${teams.length}/${t.teams_cap})`} />
      {teams.map((tm, i) => {
        const count = players.filter((p) => p.team_id === tm.id).length;
        return (
          <ChalkCard key={tm.id} data-testid={`team-row-${tm.id}`} onClick={() => setOpenTeam(tm.id)}
                     style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>{tm.seed ?? i + 1}.</b> {tm.name}
              {hub && (
                <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 2 }}>
                  {count} player{count === 1 ? "" : "s"}{tm.captain_name ? ` · Captain ${tm.captain_name}` : ""}
                </div>
              )}
            </div>
            {tm.checkin_status === "checked_in" && <Pill tone="green">Here</Pill>}
            <ChevronRight size={16} aria-hidden="true" color="var(--chalk-muted)" />
          </ChalkCard>
        );
      })}
      {!division.bracket_locked && (
        <>
          <textarea value={names} onChange={(e) => setNames(e.target.value)} rows={4}
                    placeholder="Paste team names, one per line" data-testid="teams-bulk-input"
                    style={{ width: "100%", marginTop: 12, padding: 10, borderRadius: 10,
                             border: "0.5px solid var(--chalk-border)", background: "#FBF8F2",
                             color: "var(--chalk-board)" }} />
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <ChalkActionButton label="Add" loading={busy} onClick={paste} testId="teams-bulk-add" fullWidth={false} />
            {isDirector && teams.length >= 2 && division.format === "pools" && (
              <span data-testid="pools-hint" style={{ fontSize: 12, color: "var(--chalk-muted)", alignSelf: "center" }}>
                Pool play: set up pools on the Bracket tab.</span>
            )}
            {isDirector && teams.length >= 2 && division.format !== "pools" && (
              <>
                <ChalkActionButton variant="primary" label="Build bracket" onClick={() => build("manual")}
                                   testId="bracket-build-manual" fullWidth={false} />
                <ChalkActionButton label="Random seed" onClick={() => build("random")}
                                   testId="bracket-build-random" fullWidth={false} />
              </>
            )}
          </div>
        </>
      )}
      {team && (
        <TeamSheet data={data} team={team} isDirector={isDirector} locked={division.bracket_locked}
                   onClose={() => setOpenTeam(null)} onChanged={onChanged} />
      )}
    </div>
  );
}

function RosterConflicts({ conflicts }) {
  if (!conflicts.length) return null;
  return (
    <div data-testid="roster-conflicts" style={{ background: "var(--chalk-amber-bg)", borderRadius: 12,
                                                 padding: 12, marginBottom: 12, color: "var(--chalk-amber)" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700, fontSize: 13 }}>
        <AlertTriangle size={15} aria-hidden="true" /> {conflicts.length} possible roster conflict{conflicts.length > 1 ? "s" : ""}
      </div>
      <div style={{ fontSize: 11, marginTop: 2 }}>Warning only — review before building brackets.</div>
      {conflicts.map((c, i) => (
        <div key={i} data-testid={`roster-conflict-${i}`} style={{ marginTop: 8, fontSize: 12, color: "#5A3A00" }}>
          <b>{c.reason}</b>
          <div>{c.players.map((p) => `${p.team_name}${p.division_name ? ` (${p.division_name})` : ""}${p.jersey ? ` #${p.jersey}` : ""}`).join("  ↔  ")}</div>
        </div>
      ))}
    </div>
  );
}
